import { isEqual, omit, set } from "lodash-es"
import {
  action,
  makeObservable,
  observable,
  reaction,
  runInAction,
  toJS,
} from "mobx"
import { sleep } from "polaris-enhance"

import APIAddons from "@/api/addons"
import { TimeLineItem } from "@/api/addons/type"
import APIManagement from "@/api/management"
import {
  DEFAULT_MANAGEMENT_LIST_QUERY,
  IApproveProps,
  ManagementListQueryParams,
  OptPage,
  ResentNotifyInfo,
  TableColumnData,
} from "@/api/management/type"
import {
  Action,
  IDisplayItem,
  IManagement,
  IOrderDetailInfo,
  IProcessStatusConfig,
  ISolutionItem,
  Item,
  ProductItem,
  ShowChildModalGroup,
  ShowModalGroup,
} from "@/models/IManagement"
import {
  completeGuideStep,
  RETURN_REQUEST_PROCESSED_KEY,
} from "@/pages/Dashboard/components/UserGuide/tracking"
import { ITabNav, tabNavConfig } from "@/pages/Management/config"
import { SandboxScenario } from "@/pages/Management/OrderDetail/sandbox"
import UserInfoStore from "@/stores/UserInfoStore"
import dayjs from "@/utils/dayjs"
import { sessionStore } from "@/utils/webStorage"

import DefaultOrderInfo from "./DefaultOrderInfo"

/** 列表查询条件持久化到 session 的 key（仅刷新场景保活，关闭 tab 即清空） */
const LIST_QUERY_STORAGE_KEY = "management:listQueryParams"

export default class ManagementStore implements IManagement {
  @observable public loadedDataTime = 0
  @observable public orderDetailInfo: IOrderDetailInfo =
    DefaultOrderInfo.orderInfo
  @observable public processStatus: IProcessStatusConfig[] = []
  @observable public productItem: ProductItem[] = [] // 展示的商品
  @observable public receivedItem: ProductItem[] = []
  @observable public restockedItem: ProductItem[] = []
  @observable public isSandbox = false
  @observable public sandboxScenario: undefined | SandboxScenario = undefined
  @observable public action: Action = {
    doing: [],
    doStatus: "",
    show: [],
    status: "",
    is_show_notify: false,
    is_show_reject: false,
    can_handle_req: false,
    more_action: [],
    is_archived: false,
  }
  @observable public showModalGroup: ShowModalGroup = {
    rejected_modal_show: false,
    resolved_modal_show: false,
    received_modal_show: false,
    approved_modal_show: false,
    approved_drawer_show: false,
    refund_modal_show: false,
    restock_modal_show: false,
    exchange_modal_show: false,
    carrier_service_modal_show: false,
  }
  @observable public showChildModalGroup: ShowChildModalGroup = {
    showShippoModal: false,
    showSendcloudModal: false,
    showFedexModal: false,
    showShipStationModal: false,
    showDHLModal: false,
  }
  @observable public tabSelectedIndex: number = 0

  @observable public sendApprovedRequestData: IApproveProps = {
    request_id: 0,
    return_address_id: 0,
    shipping_carrier: "",
    object_id: "",
    logistics_id: 0, // 1shippo 2shipstation
    tracking_number: "",
    return_label_type: 1, // 1 Automated 自动 2.Manual手动,
    label_url: "",
    return_label_file: {
      type: "",
      size: 0,
      name: "",
    },
    label_list: [],
  }

  @observable carrierServiceTemporaryData: any = null

  // 是否在更新label
  @observable updatingLabel = false

  @observable approveDrawerMode: "approve" | "configure" = "approve"

  // solution 列表
  @observable solutionList: ISolutionItem[] = []

  // method 列表
  @observable methodList: ISolutionItem[] = []

  // 发送给后端的特定格式的商品
  @observable handleData: IDisplayItem[] = []

  /** 列表查询参数（列表数据在页面本地维护） */
  @observable public listQueryParams: ManagementListQueryParams = {
    ...DEFAULT_MANAGEMENT_LIST_QUERY,
  }

  /** 列表是否已完成首次初始化（区分「首次进入」与「有缓存含手动清空」） */
  @observable public listQueryInitialized = false

  @observable public tableColumnSelected: string[] = []
  @observable public tableColumnChoices: Item[] = []

  @observable sortSelected: string = ""
  @observable sortDirectionSelected: string = "desc"

  @observable sortStatus: "asc" | "desc" = "desc"

  @action.bound
  setSortStatus(status: "asc" | "desc") {
    this.sortStatus = status
  }

  @action.bound
  setSandboxContext(
    isSandbox = false,
    sandboxScenario: undefined | SandboxScenario = undefined,
  ) {
    this.isSandbox = isSandbox
    this.sandboxScenario = sandboxScenario
  }

  // 设置展示物流连接状态
  @observable showTrackingStatus: number = 1

  @observable timeLineInfoLoading: boolean = false
  @action.bound
  setTimeLineInfoLoading(loading: boolean) {
    this.timeLineInfoLoading = loading
  }

  // 时间线数据
  @observable timeLineInfo: TimeLineItem[] = []

  // Custom views related state
  @observable customViews: ITabNav[] = []
  @observable currentCustomViewId: string | null = null
  @observable isLoadingCustomViews: boolean = false
  @observable customViewBeingSaved: string | null = null

  // 记录跳转进详情的id
  @observable detailId: string = ""

  /** 详情页上下条导航用的 request id 列表 */
  @observable detailRequestIds: number[] = []
  @observable detailRequestIdsLoading = false
  /** 列表两端是否还有更多数据（接口返回空或数量 < limit 即视为到底） */
  @observable hasMorePreIds = false
  @observable hasMoreNextIds = false
  private guideReportLoading = false
  private guideReactionInitialized = false

  constructor() {
    makeObservable(this)
    // Initialize tabNavConfig with default tabs
    const defaultTabs: ITabNav[] = [
      {
        id: "0",
        content: "All",
        tran_key: "All",
        filters: {},
        isCustomized: false,
      },
      {
        id: "1",
        content: "Pending review",
        tran_key: "PendingReview0",
        filters: { status: "1" },
        isCustomized: false,
      },
      {
        id: "2",
        content: "Approved",
        tran_key: "Approved",
        filters: { status: "2" },
        isCustomized: false,
      },
      {
        id: "3",
        content: "Resolved",
        tran_key: "Resolved",
        filters: { status: "3" },
        isCustomized: false,
      },
      {
        id: "4",
        content: "Rejected",
        tran_key: "Rejected",
        filters: { status: "4" },
        isCustomized: false,
      },
      {
        id: "5",
        content: "Archived",
        tran_key: "Archived",
        filters: { status: "5" },
        isCustomized: false,
      },
    ]
    tabNavConfig.setDefaultTabs(defaultTabs)
    tabNavConfig.setTabNavList(defaultTabs)

    // 从 session 同步恢复列表查询条件 + 选中 tab（早于详情页 initDetailRequestIds
    // 及列表页 TabFilter 挂载读取），保证刷新后筛选与状态 tab 都不丢；任何变更自动写回
    const saved = sessionStore.get<{
      listQueryParams?: ManagementListQueryParams
      tabSelectedIndex?: number
      listQueryInitialized?: boolean
      currentCustomViewId?: string | null
    }>(LIST_QUERY_STORAGE_KEY)
    if (saved?.listQueryParams) {
      this.listQueryParams = this.sanitizeListQueryParams({
        ...DEFAULT_MANAGEMENT_LIST_QUERY,
        ...saved.listQueryParams,
      })
    } else {
      // 无参数缓存默认30天
      this.listQueryParams = {
        ...DEFAULT_MANAGEMENT_LIST_QUERY,
        request_date: 30,
      }
    }
    // 保留用户保存的 tabSelectedIndex 原值:custom views 是异步加载的,构造器
    // 阶段 tabNavConfig.tabs 仅有默认 5 个,若保存的 index 指向 custom view,直接
    // 越界回退为 0 会丢掉选中状态;此处只恢复 int 值,由 loadCustomViews 完成后
    // 按 currentCustomViewId 反查新列表 index 再对账。
    if (typeof saved?.tabSelectedIndex === "number") {
      this.tabSelectedIndex = saved.tabSelectedIndex
    }
    // 恢复 custom view 选中态,便于 loadCustomViews 完成后对账 tabSelectedIndex
    if (saved?.currentCustomViewId !== undefined) {
      this.currentCustomViewId = saved.currentCustomViewId
    }
    this.listQueryInitialized = saved?.listQueryInitialized ?? false
    reaction(
      () => ({
        listQueryParams: toJS(this.listQueryParams),
        tabSelectedIndex: this.tabSelectedIndex,
        listQueryInitialized: this.listQueryInitialized,
        currentCustomViewId: this.currentCustomViewId,
      }),
      (snapshot) => sessionStore.set(LIST_QUERY_STORAGE_KEY, snapshot),
    )
  }

  @action.bound
  initGuideReaction(userInfoStore: UserInfoStore) {
    if (this.guideReactionInitialized) return
    this.guideReactionInitialized = true

    reaction(
      () => [
        this.orderDetailInfo.status,
        userInfoStore.guideData.return_request_processed,
      ],
      () => {
        this.completeReturnRequestGuide(userInfoStore)
      },
    )
  }

  async completeReturnRequestGuide(userInfoStore: UserInfoStore) {
    if (this.orderDetailInfo.status <= 1) return

    await this.completeReturnRequestProcessedGuide(userInfoStore)
  }

  async completeReturnRequestProcessedGuide(userInfoStore: UserInfoStore) {
    if (this.guideReportLoading) return
    if (userInfoStore.guideData.return_request_processed) return

    this.guideReportLoading = true
    try {
      await completeGuideStep(userInfoStore, RETURN_REQUEST_PROCESSED_KEY)
    } catch (e) {
      console.error("saveGuide return_request_processed error:", e)
    } finally {
      this.guideReportLoading = false
    }
  }

  /** 首次进入详情：以 cur_id 为锚点，并行拉取 pre + next 两份列表并拼接 */
  @action.bound
  async initDetailRequestIds(cur_id: number) {
    if (!cur_id) {
      this.detailRequestIds = []
      this.hasMorePreIds = false
      this.hasMoreNextIds = false
      return
    }
    this.detailRequestIdsLoading = true
    try {
      const limit = this.listQueryParams.limit ?? 10
      const [preRes, nextRes] = await Promise.all([
        APIManagement.getRequestIdList({
          ...this.listQueryParams,
          cur_id,
          opt_page: "pre",
        }),
        APIManagement.getRequestIdList({
          ...this.listQueryParams,
          cur_id,
          opt_page: "next",
        }),
      ])
      const preIds = preRes?.data?.ids ?? []
      const nextIds = nextRes?.data?.ids ?? []
      this.detailRequestIds = Array.from(
        new Set([...preIds, cur_id, ...nextIds]),
      )
      this.hasMorePreIds = preIds.length >= limit
      this.hasMoreNextIds = nextIds.length >= limit
    } finally {
      this.detailRequestIdsLoading = false
    }
  }

  /** 接近边界时增量拉取：以当前列表端点为游标，向 next/pre 方向拼接更多 id */
  @action.bound
  async loadMoreRequestIds(opt_page: OptPage) {
    if (this.detailRequestIdsLoading || !this.detailRequestIds.length) return
    if (opt_page === "next" && !this.hasMoreNextIds) return
    if (opt_page === "pre" && !this.hasMorePreIds) return

    const cur_id =
      opt_page === "next"
        ? this.detailRequestIds[this.detailRequestIds.length - 1]
        : this.detailRequestIds[0]

    this.detailRequestIdsLoading = true
    try {
      const limit = this.listQueryParams.limit ?? 10
      const { data } = await APIManagement.getRequestIdList({
        ...this.listQueryParams,
        cur_id,
        opt_page,
      })
      const newIds = data?.ids ?? []

      if (opt_page === "next") {
        this.detailRequestIds = Array.from(
          new Set([...this.detailRequestIds, ...newIds]),
        )
        this.hasMoreNextIds = newIds.length >= limit
      } else {
        this.detailRequestIds = Array.from(
          new Set([...newIds, ...this.detailRequestIds]),
        )
        this.hasMorePreIds = newIds.length >= limit
      }
    } finally {
      this.detailRequestIdsLoading = false
    }
  }

  @action.bound
  setHandleData(data: IDisplayItem[]) {
    this.handleData = data
  }

  @action.bound
  setSolutionList(data: ISolutionItem[]) {
    this.solutionList = data
  }

  @observable detailData: any = {}
  @observable resentNotifyInfo: ResentNotifyInfo | null = null

  @action.bound
  setDetailData(data: any) {
    this.detailData = data
  }

  @action.bound
  setResentNotifyInfo(info: ResentNotifyInfo | null) {
    this.resentNotifyInfo = info
  }

  @action.bound
  setMethodList(data: ISolutionItem[]) {
    this.methodList = data
  }

  @action.bound
  setUpdatingLabel(status: boolean) {
    this.updatingLabel = status
  }

  @action.bound
  setApproveDrawerContext(
    mode: "approve" | "configure" = "approve",
    isUpdatingLabel = false,
  ) {
    this.approveDrawerMode = mode
    this.updatingLabel = isUpdatingLabel
  }

  @action.bound
  openApproveDrawer(
    mode: "approve" | "configure" = "approve",
    isUpdatingLabel = false,
  ) {
    this.setApproveDrawerContext(mode, isUpdatingLabel)
    this.setModalShow("approved_drawer_show", true)
  }

  @action.bound
  updateCarrierServiceTemporaryData(data) {
    this.carrierServiceTemporaryData = data
  }

  @observable tableColumnData: TableColumnData = {
    // 所有列的排序
    columns: [
      {
        name: "return",
        selected: true,
      },
      {
        name: "customer",
        selected: true,
      },
      {
        name: "request_date",
        selected: true,
      },
      {
        name: "request_status",
        selected: true,
      },
      {
        name: "order",
        selected: true,
      },
      {
        name: "reason",
        selected: true,
      },
      {
        name: "solution",
        selected: true,
      },
      {
        name: "resolution_status",
        selected: true,
      },
      {
        name: "shipping",
        selected: true,
      },
      {
        name: "received_restocked",
        selected: false,
      },
      {
        name: "tag",
        selected: false,
      },
      {
        name: "order_date",
        selected: false,
      },
      {
        name: "destination",
        selected: false,
      },
    ],
    sort: {
      name: "request_date",
      type: "desc",
    },
  }
  @action.bound
  setTableColumnData(data: Partial<TableColumnData>) {
    this.tableColumnData = { ...this.tableColumnData, ...data }
  }

  @action.bound
  setTableColumnSelected(item: string[]): void {
    // 确保创建一个新的数组引用，让 MobX 能观察到变化
    this.tableColumnSelected = [...item]
    // 根据selected的顺序更新tableColumnData.columns的selected顺序
    this.tableColumnData.columns = this.tableColumnData.columns.map((col) => ({
      ...col,
      selected: this.tableColumnSelected.includes(col.name),
    }))
  }

  @action.bound
  setTableColumnChoices(choices: Item[]): void {
    // 确保创建一个新的数组引用，让 MobX 能观察到变化
    this.tableColumnChoices = [...choices]
    // 根据choices的id顺序更新tableColumnData.clumns的顺序
    this.tableColumnData.columns = choices.map((item) => ({
      name: item.id,
      selected: this.tableColumnSelected.includes(item.id),
    }))
  }

  @action.bound
  setTabSelectedIndex(index: number) {
    this.tabSelectedIndex = index
  }

  @action.bound
  setApprovedRequestData<T>(path: string, v: T) {
    set(this.sendApprovedRequestData, path, v)
  }

  /**
   * 设置加载完成时间
   * @param time
   */
  @action.bound
  public setLoadedDataTime(time: number): void {
    this.loadedDataTime = time
  }

  @action.bound
  setChildModalShow<T>(path: string, v: T) {
    set(this.showChildModalGroup, path, v)
  }

  @action.bound
  setModalShow<T>(path: string, v: T) {
    set(this.showModalGroup, path, v)
  }

  @action.bound
  setAction(action: Action) {
    this.setLoadedDataTime(dayjs().unix())
    this.action = action
  }

  @action.bound
  setOrderInfo(data: IOrderDetailInfo): void {
    this.orderDetailInfo = data
  }

  @action.bound
  setProcessStatus(items: IProcessStatusConfig[]): void {
    this.processStatus = items
  }

  @action.bound
  setProductItem(items: ProductItem[]): void {
    this.productItem = items
  }

  @action.bound
  setReceivedItem(items: ProductItem[]): void {
    this.receivedItem = items
  }

  @action.bound
  setRestockedItem(items: ProductItem[]): void {
    this.restockedItem = items
  }

  @action.bound
  setOrderConfig<T>(path: string, v: T) {
    set(this.orderDetailInfo, path, v)
  }
  @action.bound
  async fetchOrderInfo(id, continueFetch = false, time = 1) {
    if (time > 20) return
    const isFirstTimeFetch = time === 1
    if (isFirstTimeFetch) {
      this.setLoadedDataTime(0)
    }
    const data = await APIManagement.getOrderDetail(id, this.sandboxScenario)
    if (!data || data.code !== 1000 || !data.data) return
    if (isFirstTimeFetch || data.data.info.work_flow_refresh.apply) {
      runInAction(() => {
        const orderInfo = omit(data.data.info, [
          "process_status",
          "product_items",
        ])
        this.setDetailData(data.data)
        this.setResentNotifyInfo(data.data.resent_notify_info ?? null)
        this.setOrderInfo(orderInfo as IOrderDetailInfo)
        this.setProcessStatus(data.data.info.process_status)
        this.setProductItem(data.data.info.product_items)
        this.setRestockedItem(data.data.info.restocked_items) // 设置补货的列表
        this.setReceivedItem(data.data.info.received_items) // 设置收到货物的列表
        this.setAction(data.data.action)
        this.setSolutionList(data.data.solutionList) // 设置解决方案列表
        this.setMethodList(data.data.shippingMethodList ?? []) // 设置退回方式列表

        this.setHandleData(
          data.data.info.product_items.map((item) => {
            return {
              return_product_id: item.id,
              net_weight: item.order_product.variant.weight,
              mass_unit: item.order_product.variant.weight_unit,
              hs_code: item.order_product.variant.hs_code,
              origin_country: item.order_product.variant.origin_country,
            }
          }),
        )
      })
      // 状态更新后重新获取timeline信息， 这里是临时加个，避免改动太多引起bug
      if (!isFirstTimeFetch) {
        this.setReLoadTimeLineInfo(id)
      }
    }
    if (
      continueFetch &&
      data.data.info.work_flow_refresh.continue &&
      // 只在management获取
      window.location.pathname.includes("/return-management/order-management")
    ) {
      sleep(isFirstTimeFetch ? 2000 : 3000).then(() => {
        this.fetchOrderInfo(id, continueFetch, time + 1)
      })
    }
  }

  @action.bound
  patchListQuery(
    patch:
      | Partial<ManagementListQueryParams>
      | ((
          prev: ManagementListQueryParams,
        ) => Partial<ManagementListQueryParams>),
    force?: boolean,
  ) {
    const slice =
      typeof patch === "function" ? patch(this.listQueryParams) : patch
    this.listQueryParams = force
      ? slice
      : this.sanitizeListQueryParams({
          ...this.listQueryParams,
          ...slice,
        })
  }

  /**
   * 清理 listQueryParams 中不再使用的衍生 key。
   * 当前场景:reason 筛选器的真实数据源是 `reasons` (string[]),旧版 `reason_ids`
   * (CSV) 属于 Setting 页使用,不应进入列表查询参数。历史 session 残留的
   * `reason_ids` 在此一并丢弃,避免它顺着 sessionStore 持久化 + 写回
   * custom view,污染保存的视图快照。
   */
  private sanitizeListQueryParams(
    params: ManagementListQueryParams,
  ): ManagementListQueryParams {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { reason_ids, ...rest } = params
    return rest
  }

  /**
   * 编辑 custom view 场景下,判断当前 listQueryParams 相对
   * `tabNavConfig.tabs[tabSelectedIndex].filters` 是否发生了变更。
   * - 仅在 `tab.isCustomized === true` 时返回有意义的结果,默认 tab 一律返回 false
   * - 排除 limit / page / sort 这些不属于"视图定义"的运行时字段后再用 lodash isEqual
   *   做深比较;key 集合或任意值不同都视为 dirty
   */
  @action.bound
  isCurrentTabFiltersDirty(): boolean {
    const tab = tabNavConfig.tabs[this.tabSelectedIndex]
    if (!tab?.isCustomized) return false
    const EXCLUDED_KEYS = ["limit", "page", "sort"] as const
    const saved = omit(tab.filters ?? {}, EXCLUDED_KEYS)
    const current = omit(this.listQueryParams, EXCLUDED_KEYS)
    return !isEqual(saved, current)
  }

  @action.bound
  resetListQueryDefaults() {
    this.listQueryParams = { ...DEFAULT_MANAGEMENT_LIST_QUERY }
    this.tabSelectedIndex = 0
    this.listQueryInitialized = false
  }

  @action.bound
  markListQueryInitialized() {
    this.listQueryInitialized = true
  }

  // 重载操作时间线数据
  @action.bound
  setReLoadTimeLineInfo(requestId) {
    // 延迟1秒重新获取操作时间线数据
    // 请求套餐列表信息
    this.setTimeLineInfoLoading(true)
    const params = {
      request_id: Number(requestId),
    }
    APIAddons.getTimeLineDetails(params, this.sandboxScenario).then((res) => {
      const { code, data } = res || {}
      if (code === 1000) {
        this.timeLineInfo = data
      }
      this.setTimeLineInfoLoading(false)
    })
  }

  // 设置展示物流连接状态
  @action.bound
  setShowTrackingStatus(val: number) {
    this.showTrackingStatus = val
  }

  /**
   * Load custom views from API and update tabNavConfig
   */
  @action.bound
  async loadCustomViews(): Promise<void> {
    this.isLoadingCustomViews = true
    const viewBeingSaved = this.customViewBeingSaved
    try {
      const res = await APIManagement.getCustomViews()
      if (res.code === 1000 && res.data) {
        // Convert API response to ITabNav format
        const customTabs: ITabNav[] = res.data.map((view, index) => ({
          id: `custom-${index}`,
          content: view.name,
          filters: view.filters,
          isCustomized: true,
        }))
        // Combine default tabs and custom views
        const allTabs = [...tabNavConfig.defaultTabs, ...customTabs]
        tabNavConfig.setTabNavList(allTabs)
        runInAction(() => {
          this.customViews = customTabs

          // If we were saving a view, find and select it
          if (viewBeingSaved) {
            const newViewIndex = allTabs.findIndex(
              (tab) => tab.isCustomized && tab.content === viewBeingSaved,
            )
            if (newViewIndex !== -1) {
              this.tabSelectedIndex = newViewIndex
              const newView = allTabs[newViewIndex]
              if (newView) {
                this.currentCustomViewId = newView.id
              }
            }
            this.customViewBeingSaved = null
          }
          // If we were in a custom view, reconcile tabSelectedIndex with the new
          // tab list (refresh / custom-view 顺序变化场景):
          // - 找到对应 id:写回它在合并后列表中的新 index,确保刷新后选中态保留
          // - 找不到(被删):与原逻辑一致回退到 All tab
          else if (this.currentCustomViewId) {
            const idx = allTabs.findIndex(
              (tab) => tab.id === this.currentCustomViewId,
            )
            if (idx !== -1) {
              this.tabSelectedIndex = idx
            } else {
              this.currentCustomViewId = null
              this.tabSelectedIndex = 0
            }
          }
          // 兜底:保存的 index 越界(例如 custom view 数量变化且未记录 id)时
          // 防止后续取 tab 越界
          else if (this.tabSelectedIndex >= allTabs.length) {
            this.tabSelectedIndex = 0
          }
        })
      }
    } catch (error) {
      console.error("Failed to load custom views:", error)
    } finally {
      runInAction(() => {
        this.isLoadingCustomViews = false
      })
    }
  }

  /**
   * Save a custom view
   * @param viewName Name of the view to save
   * @param filters Filters to save with the view
   * @param oldName Optional old name if updating an existing view
   */
  @action.bound
  async saveCustomView(
    viewName: string,
    filters: Record<string, any>,
    oldName?: string,
  ): Promise<void> {
    try {
      // Mark which view we're saving so we can select it after loading
      this.customViewBeingSaved = viewName
      await APIManagement.updateCustomView({
        old_name: oldName || "",
        name: viewName,
        filters,
      })
      // Reload custom views after saving
      await this.loadCustomViews()
    } catch (error) {
      this.customViewBeingSaved = null
      console.error("Failed to save custom view:", error)
      throw error
    }
  }

  /**
   * Delete a custom view
   * @param viewName Name of the view to delete
   */
  @action.bound
  async deleteCustomView(viewName: string): Promise<void> {
    try {
      await APIManagement.deleteCustomView({ name: viewName })
      // Check if we're deleting the currently selected view
      const isDeletingCurrentView = this.currentCustomViewId !== null

      // Reload custom views after deletion - loadCustomViews will handle switching to All
      await this.loadCustomViews()
    } catch (error) {
      console.error("Failed to delete custom view:", error)
      throw error
    }
  }

  /**
   * Set the current custom view ID
   * @param id ID of the custom view to select, or null to clear
   */
  @action.bound
  setCurrentCustomView(id: string | null): void {
    this.currentCustomViewId = id
  }
}
